import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import { GridContext } from '@bingads-webui-grid/grid-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Dropdown, Input, Option } from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { Stack } from '@fluentui/react/lib/Stack';
import {
  CAMPAIGN_TYPE_DISPLAY,
  CAMPAIGN_TYPE_FEED_BASED,
  CAMPAIGN_TYPE_NATIVE,
  CAMPAIGN_TYPE_ONLINE_VIDEO,
  DEFAULT_CAMPAIGN_SUBTYPE_FILTER,
} from '../../constants/campaign';
import { useFilterDropdownStyles } from '../../styles';

const campaignTypes = [
  CAMPAIGN_TYPE_DISPLAY,
  CAMPAIGN_TYPE_NATIVE,
  CAMPAIGN_TYPE_ONLINE_VIDEO,
  CAMPAIGN_TYPE_FEED_BASED,
];

const debouncedEditableFilterDispatch = _.debounce((currentEditableFilterPayload, dispatch) => dispatch({ type: 'SET_EDITABLE_FILTER', payload: currentEditableFilterPayload }), 300);

export const SelectCampaignsToolbar = () => {
  const styles = useFilterDropdownStyles();

  const i18n = useContext(I18nContext);
  const searchBoxPlaceholder = i18n.getString(_TL_('Search campaigns'));
  const inputLabel = i18n.getString(_TL_('Campaign name search'));
  const dropdownPlaceholder = i18n.getString(_TL_('Campaign types: All'));
  const dropdownLabel = i18n.getString(_TL_('Campaign type filter'));

  const { dispatch } = useContext(GridContext);
  const [campaignSearchText, setCampaignSearchText] = useState('');
  const [selectedCampaignTypes, setSelectedCampaignTypes] = useState([]);

  const dropdownButtonText = useMemo(
    () => {
      if (selectedCampaignTypes.length === 0) {
        return dropdownPlaceholder;
      }
      const formattedSelectionText = selectedCampaignTypes.join(i18n.getString(_TL_(', ')));
      const statusText = selectedCampaignTypes.length > 1 ? i18n.getString(_TL_('Campaign types:')) : i18n.getString(_TL_('Campaign type:'));
      return `${statusText} ${formattedSelectionText}`;
    },
    [i18n, dropdownPlaceholder, selectedCampaignTypes]
  );

  const currentEditableFilterPayload = useMemo(() => {
    const filterPayloadBase = `contains(tolower(Name), '${campaignSearchText.toLowerCase()}')`;
    if (selectedCampaignTypes.length) {
      const formattedCampaignSubTypes = selectedCampaignTypes.map((campaignSubTypeDisplayText) => {
        switch (campaignSubTypeDisplayText) {
          case CAMPAIGN_TYPE_DISPLAY:
            return 'DisplayAds';
          case CAMPAIGN_TYPE_NATIVE:
            return 'AudienceStatic';
          case CAMPAIGN_TYPE_FEED_BASED:
            return "AudienceDynamicFeedBased', 'AudienceFeedBased";
          case CAMPAIGN_TYPE_ONLINE_VIDEO:
          default:
            return "AudienceVideoAds', 'BrandAwarenessVideoAds";
        }
      });
      const formattedFilter = formattedCampaignSubTypes.map(campaignSubType => `'${campaignSubType}'`).join(', ');
      return `${filterPayloadBase} and CampaignSubType in [${formattedFilter}]`;
    }
    return `${filterPayloadBase} and ${DEFAULT_CAMPAIGN_SUBTYPE_FILTER}`;
  }, [campaignSearchText, selectedCampaignTypes]);

  useEffect(() => {
    debouncedEditableFilterDispatch(currentEditableFilterPayload, dispatch);
  }, [currentEditableFilterPayload, dispatch]);

  const onChangeSearchInput = useCallback((_e, { value }) => {
    setCampaignSearchText(value);
  }, []);

  const onOptionSelect = useCallback((_e, data) => {
    setSelectedCampaignTypes(data.selectedOptions);
  }, []);

  return (
    <Stack horizontal horizontalAlign="space-between">
      <Input
        className="campaigns-search-filter"
        aria-label={inputLabel}
        contentBefore={<SearchRegular />}
        onChange={onChangeSearchInput}
        placeholder={searchBoxPlaceholder}
        value={campaignSearchText}
      />
      <Dropdown
        className="campaign-type-filter"
        aria-label={dropdownLabel}
        placeholder={dropdownPlaceholder}
        button={
          <span className={styles.truncatedText}>{dropdownButtonText}</span>
        }
        data-selectedOptions={selectedCampaignTypes.length ? selectedCampaignTypes : dropdownPlaceholder}
        multiselect
        onOptionSelect={onOptionSelect}
        selectedOptions={selectedCampaignTypes}
      >
        {campaignTypes.map(campaignType => (
          <Option
            key={campaignType}
            value={campaignType}
            className={`campaign-type-selector-option-${campaignType}`}
          >
            {i18n.getString(campaignType)}
          </Option>
        ))}
      </Dropdown>
    </Stack>
  );
};
